/**
 * Advanced Store Filters Component
 * 
 * Provides advanced filtering options for stores including filtering by store visit data
 * (competitors, stock level, low stock categories, manager contact requests)
 * 
 * @module AdvancedStoreFilters
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { 
  Filter, 
  ChevronDown, 
  ChevronUp, 
  X, 
  Users, 
  Package, 
  AlertTriangle,
  Phone,
  RotateCcw,
  Search,
  ClipboardList,
  Calendar,
  UserCheck,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows, Transitions } from '../../styles/ThemeStyles';
import { useTranslation } from 'react-i18next';

// Competitor options (same as in NotesSection)
const COMPETITOR_OPTIONS = [
  { id: 'mckenzie', name: 'Mckenzie Seeds is Exclusive' },
  { id: 'burpee', name: 'Burpee Seeds' },
  { id: 'fothergills', name: 'Fothergills Seeds' },
  { id: 'westCoast', name: 'West Coast Seeds' },
  { id: 'osc', name: 'OSC (Ontario Seed)' },
  { id: 'newfoundland', name: 'Newfoundland Seed' },
  { id: 'berton', name: 'Berton Seeds' },
  { id: 'lindenburg', name: 'Lindenburg Seeds' },
  { id: 'reneesGarden', name: "Renee's Garden" },
  { id: 'cornucopia', name: 'Cornucopia Seed' },
  { id: 'aimersOrganic', name: 'Aimers Organic' },
  { id: 'northernSeeds', name: 'Northern Seeds' },
  { id: 'gaiaSeeds', name: 'Gaia Seeds' },
  { id: 'halifaxSeeds', name: 'Halifax Seeds' },
  { id: 'others', name: 'Others' },
];

// Low stock category options
const LOW_STOCK_OPTIONS = [
  'Herbs',
  'Vegetables',
  'Flowers',
  'Bulbs',
  'Grass Seed',
  'Accessories',
];

// Store level ranges for filtering
const STORE_LEVEL_RANGES = [
  { id: 'low', label: 'Low (0-25%)', min: 0, max: 25 },
  { id: 'medium', label: 'Medium (26-50%)', min: 26, max: 50 },
  { id: 'high', label: 'High (51-75%)', min: 51, max: 75 },
  { id: 'full', label: 'Full (76-100%)', min: 76, max: 100 },
];

// Visit type options for filtering
export type VisitType = 'setup' | 'maintenance' | 'requested';
const VISIT_TYPE_OPTIONS: { id: VisitType; labelKey: string; defaultLabel: string }[] = [
  { id: 'setup', labelKey: 'notes.visitTypeSetup', defaultLabel: 'Setup Visit' },
  { id: 'maintenance', labelKey: 'notes.visitTypeMaintenance', defaultLabel: 'Maintenance Visit' },
  { id: 'requested', labelKey: 'notes.visitTypeRequested', defaultLabel: 'Store/Manager Requested Visit' },
];

export interface NotesFilterCriteria {
  competitors: string[];
  storeLevelRange: { min: number; max: number } | null;
  lowStockCategories: string[];
  needsManagerContact: boolean | null;
  hasNotes: boolean | null;
  visitTypes: VisitType[];
  visitDateRange: { from: string; to: string } | null;
  salesRepIds: string[];
}

interface AdvancedStoreFiltersProps {
  onFilterChange: (filters: NotesFilterCriteria) => void;
  currentFilters: NotesFilterCriteria;
  isLoading?: boolean;
  salesReps?: Array<{ id: string; name: string; username?: string; salesGroup?: string }>;
}

// Styled Components
const FilterContainer = styled.div`
  background: white;
  border-radius: ${BorderRadius.lg};
  box-shadow: ${Shadows.sm};
  border: 1px solid ${ThemeColors.border.light};
  margin-bottom: ${Spacing.lg};
  overflow: hidden;
`;

const FilterHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${Spacing.md} ${Spacing.lg};
  background: linear-gradient(135deg, ${ThemeColors.primary.light}15 0%, ${ThemeColors.primary.light}05 100%);
  border: none;
  cursor: pointer;
  transition: background ${Transitions.duration.short};

  &:hover {
    background: linear-gradient(135deg, ${ThemeColors.primary.light}25 0%, ${ThemeColors.primary.light}10 100%);
  }
`;

const FilterHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
`;

const FilterTitle = styled.h3`
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
`;

const FilterBadge = styled.span`
  background: ${ThemeColors.primary.main};
  color: white;
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.bold};
  padding: 2px 8px;
  border-radius: ${BorderRadius.round};
  min-width: 20px;
  text-align: center;
`;

const FilterContent = styled.div<{ isOpen: boolean }>`
  max-height: ${props => props.isOpen ? '800px' : '0'};
  overflow: hidden;
  transition: max-height ${Transitions.duration.standard} ease-in-out;
  display: flex;
  flex-direction: column;
`;

const FilterSections = styled.div`
  padding: ${Spacing.lg};
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${Spacing.lg};
  overflow-y: auto;
  flex: 1;
  min-height: 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FilterSection = styled.div`
  background: ${ThemeColors.gray[50]};
  border-radius: ${BorderRadius.md};
  padding: ${Spacing.md};
  border: 1px solid ${ThemeColors.border.light};
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  margin-bottom: ${Spacing.md};
  padding-bottom: ${Spacing.sm};
  border-bottom: 1px solid ${ThemeColors.border.light};
`;

const SectionIcon = styled.div<{ color?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: ${BorderRadius.sm};
  background: ${props => props.color || ThemeColors.primary.light};
  color: ${props => props.color ? 'white' : ThemeColors.primary.main};

  svg {
    width: 16px;
    height: 16px;
  }
`;

const SectionTitle = styled.h4`
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
  margin: 0;
  flex: 1;
`;

const SectionClear = styled.button`
  background: none;
  border: none;
  color: ${ThemeColors.text.hint};
  font-size: ${Typography.fontSize.xs};
  cursor: pointer;
  padding: 2px 6px;
  border-radius: ${BorderRadius.sm};
  transition: all ${Transitions.duration.short};

  &:hover {
    background: ${ThemeColors.error.light};
    color: ${ThemeColors.error.main};
  }
`;

const CheckboxList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs};
  max-height: 180px;
  overflow-y: auto;
  padding-right: ${Spacing.xs};

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${ThemeColors.gray[100]};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${ThemeColors.gray[300]};
    border-radius: 3px;
  }
`;

const CheckboxItem = styled.label<{ checked?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.xs} ${Spacing.sm};
  border-radius: ${BorderRadius.sm};
  cursor: pointer;
  transition: background ${Transitions.duration.short};
  background: ${props => props.checked ? ThemeColors.primary.light : 'transparent'};

  &:hover {
    background: ${props => props.checked ? ThemeColors.primary.light : ThemeColors.gray[100]};
  }
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  accent-color: ${ThemeColors.primary.main};
  cursor: pointer;
`;

const CheckboxLabel = styled.span<{ checked?: boolean }>`
  font-size: ${Typography.fontSize.sm};
  color: ${props => props.checked ? ThemeColors.primary.dark : ThemeColors.text.primary};
  flex: 1;
`;

const RangeButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.xs};
`;

const RangeButton = styled.button<{ selected?: boolean }>`
  padding: ${Spacing.xs} ${Spacing.sm};
  border: 1px solid ${props => props.selected ? ThemeColors.primary.main : ThemeColors.border.main};
  background: ${props => props.selected ? ThemeColors.primary.main : 'white'};
  color: ${props => props.selected ? 'white' : ThemeColors.text.primary};
  border-radius: ${BorderRadius.sm};
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${Transitions.duration.short};

  &:hover {
    border-color: ${ThemeColors.primary.main};
    background: ${props => props.selected ? ThemeColors.primary.dark : ThemeColors.primary.light};
  }
`;

const ToggleOption = styled.button<{ selected?: boolean; variant?: 'yes' | 'no' | 'any' }>`
  flex: 1;
  padding: ${Spacing.sm};
  border: 1px solid ${props => props.selected ? ThemeColors.primary.main : ThemeColors.border.main};
  background: ${props => {
    if (!props.selected) return 'white';
    if (props.variant === 'yes') return ThemeColors.success.main;
    if (props.variant === 'no') return ThemeColors.gray[400];
    return ThemeColors.primary.main;
  }};
  color: ${props => props.selected ? 'white' : ThemeColors.text.primary};
  border-radius: ${BorderRadius.sm};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${Transitions.duration.short};

  &:hover {
    opacity: 0.9;
  }
`;

const ToggleGroup = styled.div`
  display: flex;
  gap: ${Spacing.xs};
`;

const FilterFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing.md} ${Spacing.lg};
  background: ${ThemeColors.gray[50]};
  border-top: 1px solid ${ThemeColors.border.light};
`;

const ResetButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm} ${Spacing.md};
  border: 1px solid ${ThemeColors.border.main};
  background: white;
  color: ${ThemeColors.text.secondary};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  cursor: pointer;
  transition: all ${Transitions.duration.short};

  &:hover {
    background: ${ThemeColors.error.light};
    color: ${ThemeColors.error.main};
    border-color: ${ThemeColors.error.main};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ActiveFiltersCount = styled.span`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${Spacing.xs} ${Spacing.sm};
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.sm};
  font-size: ${Typography.fontSize.sm};
  margin-bottom: ${Spacing.sm};

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
  }
`;

const DateRangeContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm};
`;

const DateInput = styled.input`
  width: 100%;
  padding: ${Spacing.xs} ${Spacing.sm};
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.sm};
  font-size: ${Typography.fontSize.sm};

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
  }
`;

const DateLabel = styled.label`
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.secondary};
  font-weight: ${Typography.fontWeight.medium};
`;

/**
 * Get the default empty filter state
 */
export const getDefaultNotesFilters = (): NotesFilterCriteria => ({
  competitors: [],
  storeLevelRange: null,
  lowStockCategories: [],
  needsManagerContact: null,
  hasNotes: null,
  visitTypes: [],
  visitDateRange: null,
  salesRepIds: [],
});

/**
 * Advanced Store Filters Component
 */
export const AdvancedStoreFilters: React.FC<AdvancedStoreFiltersProps> = ({
  onFilterChange,
  currentFilters,
  isLoading = false,
  salesReps = [],
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [competitorSearch, setCompetitorSearch] = useState('');
  const [salesRepSearch, setSalesRepSearch] = useState('');

  // Count active filters
  const activeFilterCount = 
    currentFilters.competitors.length +
    (currentFilters.storeLevelRange ? 1 : 0) +
    currentFilters.lowStockCategories.length +
    (currentFilters.needsManagerContact !== null ? 1 : 0) +
    (currentFilters.hasNotes !== null ? 1 : 0) +
    (currentFilters.visitTypes?.length || 0) +
    (currentFilters.visitDateRange ? 1 : 0) +
    (currentFilters.salesRepIds?.length || 0);

  // Handle competitor selection
  const handleCompetitorChange = useCallback((competitorName: string, checked: boolean) => {
    const newCompetitors = checked
      ? [...currentFilters.competitors, competitorName]
      : currentFilters.competitors.filter(c => c !== competitorName);
    
    onFilterChange({
      ...currentFilters,
      competitors: newCompetitors,
    });
  }, [currentFilters, onFilterChange]);

  // Handle store level range selection
  const handleStoreLevelChange = useCallback((range: { min: number; max: number } | null) => {
    onFilterChange({
      ...currentFilters,
      storeLevelRange: currentFilters.storeLevelRange?.min === range?.min && 
                       currentFilters.storeLevelRange?.max === range?.max 
        ? null 
        : range,
    });
  }, [currentFilters, onFilterChange]);

  // Handle low stock category selection
  const handleLowStockChange = useCallback((category: string, checked: boolean) => {
    const newCategories = checked
      ? [...currentFilters.lowStockCategories, category]
      : currentFilters.lowStockCategories.filter(c => c !== category);
    
    onFilterChange({
      ...currentFilters,
      lowStockCategories: newCategories,
    });
  }, [currentFilters, onFilterChange]);

  // Handle manager contact filter
  const handleManagerContactChange = useCallback((value: boolean | null) => {
    onFilterChange({
      ...currentFilters,
      needsManagerContact: currentFilters.needsManagerContact === value ? null : value,
    });
  }, [currentFilters, onFilterChange]);

  // Handle has notes filter
  const handleHasNotesChange = useCallback((value: boolean | null) => {
    onFilterChange({
      ...currentFilters,
      hasNotes: currentFilters.hasNotes === value ? null : value,
    });
  }, [currentFilters, onFilterChange]);

  // Handle visit type selection
  const handleVisitTypeChange = useCallback((visitType: VisitType, checked: boolean) => {
    const currentVisitTypes = currentFilters.visitTypes || [];
    const newVisitTypes = checked
      ? [...currentVisitTypes, visitType]
      : currentVisitTypes.filter(v => v !== visitType);
    
    onFilterChange({
      ...currentFilters,
      visitTypes: newVisitTypes,
    });
  }, [currentFilters, onFilterChange]);

  // Reset all filters
  const handleResetAll = useCallback(() => {
    onFilterChange(getDefaultNotesFilters());
  }, [onFilterChange]);

  // Handle visit date range change
  const handleVisitDateRangeChange = useCallback((field: 'from' | 'to', value: string) => {
    const currentRange = currentFilters.visitDateRange || { from: '', to: '' };
    const newRange = {
      ...currentRange,
      [field]: value
    };
    
    onFilterChange({
      ...currentFilters,
      visitDateRange: (newRange.from || newRange.to) ? newRange : null,
    });
  }, [currentFilters, onFilterChange]);

  // Handle sales rep selection
  const handleSalesRepChange = useCallback((salesRepId: string, checked: boolean) => {
    const currentReps = currentFilters.salesRepIds || [];
    const newReps = checked
      ? [...currentReps, salesRepId]
      : currentReps.filter(id => id !== salesRepId);
    
    onFilterChange({
      ...currentFilters,
      salesRepIds: newReps,
    });
  }, [currentFilters, onFilterChange]);

  // Clear section filters
  const clearCompetitors = () => onFilterChange({ ...currentFilters, competitors: [] });
  const clearStoreLevel = () => onFilterChange({ ...currentFilters, storeLevelRange: null });
  const clearLowStock = () => onFilterChange({ ...currentFilters, lowStockCategories: [] });
  const clearVisitTypes = () => onFilterChange({ ...currentFilters, visitTypes: [] });
  const clearVisitDateRange = () => onFilterChange({ ...currentFilters, visitDateRange: null });
  const clearSalesReps = () => onFilterChange({ ...currentFilters, salesRepIds: [] });

  // Filter competitors by search
  const filteredCompetitors = COMPETITOR_OPTIONS.filter(c =>
    c.name.toLowerCase().includes(competitorSearch.toLowerCase())
  );

  return (
    <FilterContainer>
      <FilterHeader onClick={() => setIsOpen(!isOpen)}>
        <FilterHeaderLeft>
          <Filter size={20} color={ThemeColors.primary.main} />
          <FilterTitle>
            {t('filters.advancedFilters', 'Advanced Store Visit Filters')}
            {activeFilterCount > 0 && (
              <FilterBadge>{activeFilterCount}</FilterBadge>
            )}
          </FilterTitle>
        </FilterHeaderLeft>
        {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </FilterHeader>

      <FilterContent isOpen={isOpen}>
        <FilterSections>
          {/* Competitors Filter */}
          <FilterSection>
            <SectionHeader>
              <SectionIcon color={ThemeColors.info.main}>
                <Users />
              </SectionIcon>
              <SectionTitle>{t('filters.competitors', 'Competitors Spotted')}</SectionTitle>
              {currentFilters.competitors.length > 0 && (
                <SectionClear onClick={clearCompetitors}>Clear</SectionClear>
              )}
            </SectionHeader>
            <SearchInput
              type="text"
              placeholder="Search competitors..."
              value={competitorSearch}
              onChange={(e) => setCompetitorSearch(e.target.value)}
            />
            <CheckboxList>
              {filteredCompetitors.map((competitor) => {
                const isChecked = currentFilters.competitors.includes(competitor.name);
                return (
                  <CheckboxItem key={competitor.id} checked={isChecked}>
                    <Checkbox
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => handleCompetitorChange(competitor.name, e.target.checked)}
                    />
                    <CheckboxLabel checked={isChecked}>{competitor.name}</CheckboxLabel>
                  </CheckboxItem>
                );
              })}
            </CheckboxList>
          </FilterSection>

          {/* Visit Type Filter */}
          <FilterSection>
            <SectionHeader>
              <SectionIcon color={ThemeColors.primary.main}>
                <ClipboardList />
              </SectionIcon>
              <SectionTitle>{t('filters.visitType', 'Visit Type')}</SectionTitle>
              {(currentFilters.visitTypes?.length || 0) > 0 && (
                <SectionClear onClick={clearVisitTypes}>Clear</SectionClear>
              )}
            </SectionHeader>
            <CheckboxList>
              {VISIT_TYPE_OPTIONS.map((visitTypeOption) => {
                const isChecked = (currentFilters.visitTypes || []).includes(visitTypeOption.id);
                return (
                  <CheckboxItem key={visitTypeOption.id} checked={isChecked}>
                    <Checkbox
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => handleVisitTypeChange(visitTypeOption.id, e.target.checked)}
                    />
                    <CheckboxLabel checked={isChecked}>{t(visitTypeOption.labelKey, visitTypeOption.defaultLabel)}</CheckboxLabel>
                  </CheckboxItem>
                );
              })}
            </CheckboxList>
          </FilterSection>

          {/* Store Level Filter */}
          <FilterSection>
            <SectionHeader>
              <SectionIcon color={ThemeColors.success.main}>
                <Package />
              </SectionIcon>
              <SectionTitle>{t('filters.storeLevel', 'McKenzie Stock Level')}</SectionTitle>
              {currentFilters.storeLevelRange && (
                <SectionClear onClick={clearStoreLevel}>Clear</SectionClear>
              )}
            </SectionHeader>
            <RangeButtonGroup>
              {STORE_LEVEL_RANGES.map((range) => {
                const isSelected = 
                  currentFilters.storeLevelRange?.min === range.min &&
                  currentFilters.storeLevelRange?.max === range.max;
                return (
                  <RangeButton
                    key={range.id}
                    selected={isSelected}
                    onClick={() => handleStoreLevelChange({ min: range.min, max: range.max })}
                  >
                    {range.label}
                  </RangeButton>
                );
              })}
            </RangeButtonGroup>
          </FilterSection>

          {/* Low Stock Categories Filter */}
          <FilterSection>
            <SectionHeader>
              <SectionIcon color={ThemeColors.warning.main}>
                <AlertTriangle />
              </SectionIcon>
              <SectionTitle>{t('filters.lowStock', 'Low Stock Reported')}</SectionTitle>
              {currentFilters.lowStockCategories.length > 0 && (
                <SectionClear onClick={clearLowStock}>Clear</SectionClear>
              )}
            </SectionHeader>
            <CheckboxList>
              {LOW_STOCK_OPTIONS.map((category) => {
                const isChecked = currentFilters.lowStockCategories.includes(category);
                return (
                  <CheckboxItem key={category} checked={isChecked}>
                    <Checkbox
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => handleLowStockChange(category, e.target.checked)}
                    />
                    <CheckboxLabel checked={isChecked}>{category}</CheckboxLabel>
                  </CheckboxItem>
                );
              })}
            </CheckboxList>
          </FilterSection>

          {/* Manager Contact Filter */}
          <FilterSection>
            <SectionHeader>
              <SectionIcon color={ThemeColors.error.main}>
                <Phone />
              </SectionIcon>
              <SectionTitle>{t('filters.managerContact', 'Manager Contact Requested')}</SectionTitle>
            </SectionHeader>
            <ToggleGroup>
              <ToggleOption
                variant="any"
                selected={currentFilters.needsManagerContact === null}
                onClick={() => handleManagerContactChange(null)}
              >
                Any
              </ToggleOption>
              <ToggleOption
                variant="yes"
                selected={currentFilters.needsManagerContact === true}
                onClick={() => handleManagerContactChange(true)}
              >
                Yes
              </ToggleOption>
              <ToggleOption
                variant="no"
                selected={currentFilters.needsManagerContact === false}
                onClick={() => handleManagerContactChange(false)}
              >
                No
              </ToggleOption>
            </ToggleGroup>
          </FilterSection>

          {/* Has Visits Filter */}
          <FilterSection>
            <SectionHeader>
              <SectionIcon color={ThemeColors.info.main}>
                <CheckCircle />
              </SectionIcon>
              <SectionTitle>Has Visits</SectionTitle>
            </SectionHeader>
            <ToggleGroup>
              <ToggleOption
                variant="any"
                selected={currentFilters.hasNotes === null}
                onClick={() => handleHasNotesChange(null)}
              >
                Any
              </ToggleOption>
              <ToggleOption
                variant="yes"
                selected={currentFilters.hasNotes === true}
                onClick={() => handleHasNotesChange(true)}
              >
                Yes
              </ToggleOption>
              <ToggleOption
                variant="no"
                selected={currentFilters.hasNotes === false}
                onClick={() => handleHasNotesChange(false)}
              >
                No
              </ToggleOption>
            </ToggleGroup>
          </FilterSection>

          {/* Visit Date Range Filter */}
          <FilterSection>
            <SectionHeader>
              <SectionIcon color={ThemeColors.primary.main}>
                <Calendar />
              </SectionIcon>
              <SectionTitle>Visit Date Range</SectionTitle>
              {currentFilters.visitDateRange && (
                <SectionClear onClick={clearVisitDateRange}>Clear</SectionClear>
              )}
            </SectionHeader>
            <DateRangeContainer>
              <div>
                <DateLabel>From Date</DateLabel>
                <DateInput
                  type="date"
                  value={currentFilters.visitDateRange?.from || ''}
                  onChange={(e) => handleVisitDateRangeChange('from', e.target.value)}
                />
              </div>
              <div>
                <DateLabel>To Date</DateLabel>
                <DateInput
                  type="date"
                  value={currentFilters.visitDateRange?.to || ''}
                  onChange={(e) => handleVisitDateRangeChange('to', e.target.value)}
                  min={currentFilters.visitDateRange?.from || undefined}
                />
              </div>
            </DateRangeContainer>
          </FilterSection>

          {/* Sales Rep Filter */}
          {salesReps.length > 0 && (
            <FilterSection>
              <SectionHeader>
                <SectionIcon color={ThemeColors.primary.main}>
                  <UserCheck />
                </SectionIcon>
                <SectionTitle>Sales Representative</SectionTitle>
                {(currentFilters.salesRepIds?.length || 0) > 0 && (
                  <SectionClear onClick={clearSalesReps}>Clear</SectionClear>
                )}
              </SectionHeader>
              <SearchInput
                type="text"
                placeholder="Search sales reps..."
                value={salesRepSearch}
                onChange={(e) => setSalesRepSearch(e.target.value)}
              />
              <CheckboxList>
                {salesReps
                  .filter(rep => 
                    rep.name.toLowerCase().includes(salesRepSearch.toLowerCase()) ||
                    rep.username?.toLowerCase().includes(salesRepSearch.toLowerCase()) ||
                    rep.salesGroup?.toLowerCase().includes(salesRepSearch.toLowerCase())
                  )
                  .map((rep) => {
                    const isChecked = (currentFilters.salesRepIds || []).includes(rep.id);
                    return (
                      <CheckboxItem key={rep.id} checked={isChecked}>
                        <Checkbox
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => handleSalesRepChange(rep.id, e.target.checked)}
                        />
                        <CheckboxLabel checked={isChecked}>
                          {rep.name} {rep.salesGroup && `(${rep.salesGroup})`}
                        </CheckboxLabel>
                      </CheckboxItem>
                    );
                  })}
              </CheckboxList>
            </FilterSection>
          )}
        </FilterSections>

        <FilterFooter>
          <ActiveFiltersCount>
            {activeFilterCount > 0 
              ? `${activeFilterCount} filter${activeFilterCount > 1 ? 's' : ''} active`
              : 'No filters active'
            }
          </ActiveFiltersCount>
          <ResetButton onClick={handleResetAll} disabled={activeFilterCount === 0}>
            <RotateCcw />
            Reset All Filters
          </ResetButton>
        </FilterFooter>
      </FilterContent>
    </FilterContainer>
  );
};

export default AdvancedStoreFilters;
